/**
 * Hard-capped object pools. ARCHITECTURE.md §11.1.
 *
 * Pools never grow at runtime and never allocate in update/fixedUpdate. When a pool is
 * exhausted, the oldest live entry is recycled (LRU) and a starvation counter increments —
 * a visibly degraded effect is always better than an unbounded allocation.
 */

export class Pool {
  /**
   * @param {string} name
   * @param {() => object} factory called `capacity` times at construction only
   * @param {(obj: object) => void} reset returns an object to a clean state
   * @param {number} capacity hard cap
   * @param {{ counter?: (name: string, n?: number) => void }} [metrics]
   */
  constructor(name, factory, reset, capacity, metrics = null) {
    this.name = name;
    this.capacity = capacity;
    this._reset = reset;
    this._metrics = metrics;
    this._items = new Array(capacity);
    this._free = new Array(capacity);
    this._freeCount = capacity;
    /** Ordered list of live item indices, oldest first — the LRU queue. */
    this._live = new Array(capacity);
    this._liveCount = 0;
    this._inLive = new Uint8Array(capacity);

    for (let i = 0; i < capacity; i++) {
      const item = factory();
      item.__poolIndex = i;
      item.__poolActive = false;
      this._items[i] = item;
      this._free[i] = capacity - 1 - i; // pop from the end -> ascending index order
    }
    this.starved = 0;
    this.peakLive = 0;
  }

  get liveCount() {
    return this._liveCount;
  }

  get freeCount() {
    return this._freeCount;
  }

  /** Acquires an object. Never returns null; recycles the oldest live entry if full. */
  acquire() {
    let idx;
    if (this._freeCount > 0) {
      idx = this._free[--this._freeCount];
    } else {
      // LRU recycle: steal the oldest live entry.
      idx = this._live[0];
      this._shiftLive();
      this.starved++;
      if (this._metrics) this._metrics.counter(`pool.${this.name}.starved`, 1);
    }
    const item = this._items[idx];
    this._reset(item);
    item.__poolActive = true;
    this._live[this._liveCount++] = idx;
    this._inLive[idx] = 1;
    if (this._liveCount > this.peakLive) this.peakLive = this._liveCount;
    return item;
  }

  /** Returns an object to the pool. Safe to call twice. */
  release(item) {
    const idx = item.__poolIndex;
    if (idx === undefined || !item.__poolActive) return;
    item.__poolActive = false;
    this._reset(item);
    this._removeLive(idx);
    this._free[this._freeCount++] = idx;
  }

  /** Releases everything. Used on round reset and scene teardown. */
  releaseAll() {
    for (let i = 0; i < this.capacity; i++) {
      const item = this._items[i];
      if (item.__poolActive) {
        item.__poolActive = false;
        this._reset(item);
      }
      this._free[i] = this.capacity - 1 - i;
      this._inLive[i] = 0;
    }
    this._freeCount = this.capacity;
    this._liveCount = 0;
  }

  /**
   * Iterates live items. The callback may release the current item safely; iteration
   * walks a snapshot of the live count from the end so removals do not skip entries.
   */
  forEachLive(fn) {
    for (let i = this._liveCount - 1; i >= 0; i--) {
      const idx = this._live[i];
      const item = this._items[idx];
      if (item.__poolActive) fn(item);
    }
  }

  /** Direct indexed access to the backing store — for GPU buffer writers. */
  itemAt(i) {
    return this._items[i];
  }

  liveIndexAt(i) {
    return this._live[i];
  }

  _shiftLive() {
    const idx = this._live[0];
    for (let i = 1; i < this._liveCount; i++) this._live[i - 1] = this._live[i];
    this._liveCount--;
    this._inLive[idx] = 0;
  }

  _removeLive(idx) {
    if (!this._inLive[idx]) return;
    let found = -1;
    for (let i = 0; i < this._liveCount; i++) {
      if (this._live[i] === idx) {
        found = i;
        break;
      }
    }
    if (found < 0) return;
    for (let i = found + 1; i < this._liveCount; i++) this._live[i - 1] = this._live[i];
    this._liveCount--;
    this._inLive[idx] = 0;
  }

  stats() {
    return {
      name: this.name,
      capacity: this.capacity,
      live: this._liveCount,
      peak: this.peakLive,
      starved: this.starved,
    };
  }
}

export class PoolRegistry {
  constructor(metrics = null) {
    this._pools = new Map();
    this._metrics = metrics;
  }

  create(name, factory, reset, capacity) {
    if (this._pools.has(name)) return this._pools.get(name);
    const pool = new Pool(name, factory, reset, capacity, this._metrics);
    this._pools.set(name, pool);
    return pool;
  }

  get(name) {
    return this._pools.get(name) || null;
  }

  has(name) {
    return this._pools.has(name);
  }

  /** Drops a pool entirely — only on subsystem dispose. */
  destroy(name) {
    const pool = this._pools.get(name);
    if (pool) pool.releaseAll();
    this._pools.delete(name);
  }

  releaseAll() {
    for (const pool of this._pools.values()) pool.releaseAll();
  }

  stats() {
    const out = [];
    for (const pool of this._pools.values()) out.push(pool.stats());
    return out;
  }

  /** True if every pool stayed within capacity for the whole session. */
  allBounded() {
    for (const pool of this._pools.values()) if (pool.starved > 0) return false;
    return true;
  }
}
